import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import '../styles/AnalyticsDashboard.css';

interface AnalyticsDashboardProps {
  token: string;
  apiBase: string;
}

type AnalyticsTab = 'overview' | 'engagement' | 'traffic' | 'visitors' | 'system';

interface VisitorAnalytics {
  totalPageViews: number;
  uniqueVisitors: number;
  newVisitors: number;
  returningVisitors: number;
  todayViews: number;
  dailyVisits: { date: string; count: number }[];
  hourlyDistribution: { hour: number; count: number }[];
  referrerBreakdown: { referrer: string; count: number }[];
  utmBreakdown: { utm_source: string | null; utm_medium: string | null; utm_campaign: string | null; count: number }[];
  locationBreakdown: { location: string; count: number }[];
  recentVisitors: {
    ip_hash: string;
    ip_address: string;
    user_agent: string;
    referrer: string;
    page_path: string;
    timestamp: string;
    isReturnVisitor: boolean;
    location: string | null;
  }[];
}

interface EngagementData {
  avgSessionDuration: number;
  sessionDurationBuckets: { label: string; count: number }[];
  scrollDepthAvg: number;
  scrollBreakdown: { depth: number; label: string; count: number }[];
  photoStats: { photoIndex: number; photoFilename: string; views: number; totalDuration: number }[];
  galleryOpenCount: number;
  totalSessions: number;
}

interface SystemMetrics {
  cpu: { usage: number; cores: number };
  memory: { total: number; used: number; free: number; usagePercent: number };
  disk: { total: number; used: number; free: number; usagePercent: number };
  network: { received: number; transmitted: number };
  uptime: number;
}

const PIE_COLORS = ['#40916C', '#52B788', '#95D5B2', '#B7E4C7'];

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-panel" onClick={e => e.stopPropagation()}>
      <div className="modal-header">
        <h3>{title}</h3>
        <button className="modal-close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="modal-body">{children}</div>
    </div>
  </div>
);

interface StatCardProps {
  icon: React.ReactNode;
  iconClass: string;
  value: string | number;
  label: string;
  modalKey?: string;
  loading?: boolean;
  compact?: boolean;
  onModalOpen?: (key: string) => void;
}

const StatCard: React.FC<StatCardProps> = ({ icon, iconClass, value, label, modalKey, loading, compact, onModalOpen }) => (
  <div
    className={`stat-card ${modalKey ? 'clickable' : ''}`}
    onClick={() => modalKey && onModalOpen?.(modalKey)}
    title={modalKey ? 'Click for details' : undefined}
  >
    <div className={`stat-icon ${iconClass}`}>{icon}</div>
    <div className="stat-content">
      <span className={compact ? 'stat-value stat-value-compact' : 'stat-value'}>{loading ? '...' : value}</span>
      <span className="stat-label">{label}</span>
    </div>
    {modalKey && (
      <div className="stat-expand">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
        </svg>
      </div>
    )}
  </div>
);

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ token, apiBase }) => {
  const [analytics, setAnalytics] = useState<VisitorAnalytics | null>(null);
  const [engagement, setEngagement] = useState<EngagementData | null>(null);
  const [system, setSystem] = useState<SystemMetrics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [engagementLoading, setEngagementLoading] = useState(true);
  const [systemLoading, setSystemLoading] = useState(true);
  const [dateRange, setDateRange] = useState<7 | 30 | 0>(0);
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('overview');
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [showAllVisitors, setShowAllVisitors] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    try {
      const tz = new Date().getTimezoneOffset();
      const res = await fetch(`${apiBase}/analytics/visitors?days=${dateRange}&tz=${tz}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setAnalytics(await res.json());
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [apiBase, token, dateRange]);

  const fetchEngagement = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/analytics/engagement?days=${dateRange}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setEngagement(await res.json());
    } catch (err) {
      console.error('Failed to fetch engagement:', err);
    } finally {
      setEngagementLoading(false);
    }
  }, [apiBase, token, dateRange]);

  const fetchSystem = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/analytics/system`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setSystem(await res.json());
    } catch (err) {
      console.error('Failed to fetch system metrics:', err);
    } finally {
      setSystemLoading(false);
    }
  }, [apiBase, token]);

  useEffect(() => {
    setAnalyticsLoading(true);
    setEngagementLoading(true);
    fetchAnalytics();
    fetchEngagement();
    fetchSystem();
    const sysInterval = setInterval(fetchSystem, 5000);
    const analyticsInterval = setInterval(() => { fetchAnalytics(); fetchEngagement(); }, 30000);
    return () => { clearInterval(sysInterval); clearInterval(analyticsInterval); };
  }, [fetchAnalytics, fetchEngagement, fetchSystem]);

  // Helpers
  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const formatTimestamp = (ts: string) => {
    const utc = ts.endsWith('Z') ? ts : ts + 'Z';
    return new Date(utc).toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short'
    });
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  };

  const formatAvgDuration = (seconds: number) => {
    const safe = Math.max(0, Math.round(seconds || 0));
    const m = Math.floor(safe / 60);
    const s = safe % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getBrowserFromUA = (ua: string) => {
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('Safari')) return 'Safari';
    return 'Other';
  };

  const newVsReturning = () => {
    if (!analytics) return [{ name: 'New', value: 0 }, { name: 'Returning', value: 0 }];
    const total = analytics.newVisitors + analytics.returningVisitors;
    if (total === 0) return [{ name: 'New', value: 0 }, { name: 'Returning', value: 0 }];
    return [
      { name: 'New', value: analytics.newVisitors },
      { name: 'Returning', value: analytics.returningVisitors }
    ];
  };

  const newPercent = analytics
    ? Math.round((analytics.newVisitors / Math.max(analytics.newVisitors + analytics.returningVisitors, 1)) * 100)
    : 0;

  const hourlyData = Array.from({ length: 24 }, (_, i) => {
    const existing = analytics?.hourlyDistribution.find(h => h.hour === i);
    return { hour: `${i}:00`, count: existing?.count || 0 };
  });

  const tooltipStyle = {
    contentStyle: { backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#1e293b' },
    labelStyle: { color: '#334155', fontWeight: 500 },
    itemStyle: { color: '#2D6A4F' },
    cursor: { fill: 'rgba(64, 145, 108, 0.08)' }
  };

  // ---- TAB RENDERS ----

  const renderOverviewTab = () => (
    <>
      <div className="stats-grid">
        <StatCard
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
          iconClass="visitors"
          value={analytics?.uniqueVisitors || 0}
          label="Unique Visitors"
          modalKey="unique-visitors"
          onModalOpen={setActiveModal}
          loading={analyticsLoading}
        />
        <StatCard
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
          iconClass="views"
          value={analytics?.totalPageViews || 0}
          label="Page Views"
          modalKey="page-views"
          onModalOpen={setActiveModal}
          loading={analyticsLoading}
        />
        <StatCard
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>}
          iconClass="today"
          value={analytics?.todayViews || 0}
          label="Today's Views"
          loading={analyticsLoading}
        />
        <StatCard
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>}
          iconClass="ratio"
          value={`${newPercent}%`}
          label="New Visitors"
          modalKey="new-vs-returning"
          onModalOpen={setActiveModal}
          loading={analyticsLoading}
        />
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <h3>Visitor Timeline</h3>
          {analyticsLoading ? <div className="chart-loading"><div className="loading-spinner" /></div>
            : analytics?.dailyVisits && analytics.dailyVisits.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={analytics.dailyVisits.map(d => ({ ...d, date: formatDate(d.date) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip {...tooltipStyle} />
                  <Line type="monotone" dataKey="count" stroke="#40916C" strokeWidth={2} dot={{ fill: '#40916C' }} name="Visits" isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : <div className="no-data">No visitor data yet</div>}
        </div>

        <div className="chart-card">
          <h3>Hourly Distribution</h3>
          {analyticsLoading ? <div className="chart-loading"><div className="loading-spinner" /></div> : (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="hour" stroke="#94a3b8" fontSize={10} interval={3} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="count" fill="#2D6A4F" name="Visits" isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </>
  );

  const renderEngagementTab = () => (
    <>
      <div className="stats-grid">
        <StatCard
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
          iconClass="visitors"
          value={engagementLoading ? '...' : formatDuration(engagement?.avgSessionDuration || 0)}
          label="Avg Session Duration"
          modalKey="avg-session"
          onModalOpen={setActiveModal}
        />
        <StatCard
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>}
          iconClass="views"
          value={engagementLoading ? '...' : `${engagement?.scrollDepthAvg || 0}%`}
          label="Avg Scroll Depth"
          modalKey="scroll-depth"
          onModalOpen={setActiveModal}
        />
        <StatCard
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>}
          iconClass="today"
          value={engagementLoading ? '...' : (engagement?.photoStats.reduce((a, b) => a + b.views, 0) || 0)}
          label="Photo Views"
          modalKey="photo-views"
          onModalOpen={setActiveModal}
        />
        <StatCard
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>}
          iconClass="ratio"
          value={engagementLoading ? '...' : (engagement?.galleryOpenCount || 0)}
          label="Gallery Opens"
        />
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <h3>Session Duration Breakdown</h3>
          {engagementLoading ? <div className="chart-loading"><div className="loading-spinner" /></div>
            : engagement && engagement.sessionDurationBuckets.some(b => b.count > 0) ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={engagement.sessionDurationBuckets}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip {...tooltipStyle} />
                  <Bar dataKey="count" fill="#40916C" name="Sessions" isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="no-data">No session data yet</div>}
        </div>

        <div className="chart-card">
          <h3>Scroll Depth Milestones</h3>
          {engagementLoading ? <div className="chart-loading"><div className="loading-spinner" /></div>
            : engagement && engagement.scrollBreakdown.some(s => s.count > 0) ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={engagement.scrollBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip {...tooltipStyle} />
                  <Bar dataKey="count" fill="#2D6A4F" name="Reached" isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="no-data">No scroll data yet</div>}
        </div>
      </div>

      {engagement && engagement.photoStats.length > 0 && (
        <div className="chart-card">
          <h3>Photo Views</h3>
          <div className="photo-views-grid">
            {engagement.photoStats.map((p, i) => (
              <div key={p.photoFilename} className="photo-views-item">
                <div className="photo-views-rank">#{i + 1}</div>
                <img
                  src={`${apiBase}/uploads/${p.photoFilename}`}
                  alt={p.photoFilename}
                  className="photo-views-thumb"
                />
                <div className="photo-views-badge">
                  <span className="photo-views-badge-views">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="badge-icon"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    {p.views}
                  </span>
                  <span className="photo-views-badge-sep" />
                  <span className="photo-views-badge-duration">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="badge-icon"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    {formatAvgDuration(p.totalDuration ?? 0)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  const renderTrafficTab = () => {
    const directCount = analytics?.referrerBreakdown.find(r => r.referrer === 'Direct')?.count || 0;
    const totalReferrals = analytics?.referrerBreakdown.reduce((a, b) => a + b.count, 0) || 0;
    const topReferrer = analytics?.referrerBreakdown.filter(r => r.referrer !== 'Direct')[0];

    return (
      <>
        <div className="stats-grid">
          <StatCard
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>}
            iconClass="visitors"
            value={analyticsLoading ? '...' : directCount}
            label="Direct Visits"
          />
          <StatCard
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>}
            iconClass="views"
            value={analyticsLoading ? '...' : (topReferrer?.referrer || 'None')}
            label="Top Referrer"
            compact
          />
          <StatCard
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>}
            iconClass="today"
            value={analyticsLoading ? '...' : (analytics?.utmBreakdown.reduce((a, b) => a + b.count, 0) || 0)}
            label="UTM Sessions"
          />
          <StatCard
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>}
            iconClass="ratio"
            value={analyticsLoading ? '...' : `${100 - newPercent}%`}
            label="Return Rate"
          />
        </div>

        <div className="charts-row">
          <div className="chart-card">
            <h3>Traffic Sources</h3>
            {analyticsLoading ? <div className="chart-loading"><div className="loading-spinner" /></div>
              : analytics?.referrerBreakdown && analytics.referrerBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analytics.referrerBreakdown.slice(0, 8)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" stroke="#94a3b8" fontSize={12} />
                    <YAxis type="category" dataKey="referrer" stroke="#94a3b8" fontSize={11} width={100} />
                    <Tooltip {...tooltipStyle} />
                    <Bar dataKey="count" fill="#40916C" name="Visits" isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="no-data">No referrer data yet</div>}
          </div>

          <div className="chart-card">
            <h3>New vs Returning</h3>
            {analyticsLoading ? <div className="chart-loading"><div className="loading-spinner" /></div>
              : (analytics?.newVisitors || 0) + (analytics?.returningVisitors || 0) > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={newVsReturning()} cx="50%" cy="48%" outerRadius={85} dataKey="value" label={false} labelLine={false}>
                      {newVsReturning().map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip {...tooltipStyle} formatter={(value: number, name: string) => {
                      const total = (analytics?.newVisitors || 0) + (analytics?.returningVisitors || 0);
                      const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                      return [`${value} (${pct}%)`, name];
                    }} />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="no-data">No visitor data yet</div>}
          </div>
        </div>

        {analytics && analytics.utmBreakdown.length > 0 && (
          <div className="table-card">
            <h3>UTM Campaigns</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Medium</th>
                  <th>Campaign</th>
                  <th>Visits</th>
                </tr>
              </thead>
              <tbody>
                {analytics.utmBreakdown.map((utm, i) => (
                  <tr key={i}>
                    <td>{utm.utm_source || '-'}</td>
                    <td>{utm.utm_medium || '-'}</td>
                    <td>{utm.utm_campaign || '-'}</td>
                    <td>{utm.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  const renderVisitorsTab = () => (
    <>
      <div className="charts-row">
        {analytics && analytics.locationBreakdown.length > 0 && (
          <div className="chart-card">
            <h3>Top Locations</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={analytics.locationBreakdown.slice(0, 8)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#94a3b8" fontSize={12} />
                <YAxis type="category" dataKey="location" stroke="#94a3b8" fontSize={11} width={120} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="count" fill="#40916C" name="Visits" isAnimationActive animationDuration={1000} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="section-header">
        <h3>Recent Visitors</h3>
      </div>
      <div className="visitors-table-container">
        {analyticsLoading ? (
          <div className="table-loading"><div className="loading-spinner" /></div>
        ) : analytics?.recentVisitors && analytics.recentVisitors.length > 0 ? (
          <>
            <table className="visitors-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>IP Address</th>
                  <th>Region</th>
                  <th>Browser</th>
                  <th>Referrer</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {(showAllVisitors ? analytics.recentVisitors : analytics.recentVisitors.slice(0, 10)).map((v, i) => (
                  <tr key={i}>
                    <td>{formatTimestamp(v.timestamp)}</td>
                    <td className="ip-cell">{v.ip_address || 'unknown'}</td>
                    <td className="location-cell">{v.location || '-'}</td>
                    <td>{getBrowserFromUA(v.user_agent)}</td>
                    <td className="referrer-cell">
                      {v.referrer ? (() => { try { return new URL(v.referrer).hostname; } catch { return 'Direct'; } })() : 'Direct'}
                    </td>
                    <td>
                      <span className={`visitor-badge ${v.isReturnVisitor ? 'returning' : 'new'}`}>
                        {v.isReturnVisitor ? 'Returning' : 'New'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {analytics.recentVisitors.length > 10 && (
              <div className="see-more-container">
                <button className="see-more-button" onClick={() => setShowAllVisitors(!showAllVisitors)}>
                  {showAllVisitors ? 'Show Less' : `See More (${analytics.recentVisitors.length - 10} more)`}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="no-data">No visitors recorded yet</div>
        )}
      </div>
    </>
  );

  const renderSystemTab = () => (
    <>
      <div className="section-header">
        <h3>System Metrics</h3>
        <span className="live-indicator"><span className="pulse" />Live</span>
      </div>
      <div className="system-grid">
        <div className="system-card">
          <div className="system-header">
            <span className="system-label">CPU Usage</span>
            <span className="system-value">{systemLoading ? '...' : `${system?.cpu.usage || 0}%`}</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill cpu" style={{ width: `${system?.cpu.usage || 0}%` }} />
          </div>
          <span className="system-detail">{system?.cpu.cores || 0} cores</span>
        </div>

        <div className="system-card">
          <div className="system-header">
            <span className="system-label">Memory</span>
            <span className="system-value">{systemLoading ? '...' : `${system?.memory.usagePercent || 0}%`}</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill memory" style={{ width: `${system?.memory.usagePercent || 0}%` }} />
          </div>
          <span className="system-detail">{system ? `${system.memory.used} MB / ${system.memory.total} MB` : '...'}</span>
        </div>

        <div className="system-card">
          <div className="system-header">
            <span className="system-label">Disk</span>
            <span className="system-value">{systemLoading ? '...' : `${system?.disk.usagePercent || 0}%`}</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill disk" style={{ width: `${system?.disk.usagePercent || 0}%` }} />
          </div>
          <span className="system-detail">{system ? `${system.disk.used} GB / ${system.disk.total} GB` : '...'}</span>
        </div>

        <div className="system-card">
          <div className="system-header">
            <span className="system-label">Network I/O</span>
            <span className="system-value uptime">{systemLoading ? '...' : formatUptime(system?.uptime || 0)}</span>
          </div>
          <div className="network-stats">
            <div className="network-stat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
              <span>{system?.network.transmitted || 0} MB</span>
            </div>
            <div className="network-stat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12l7 7 7-7"/></svg>
              <span>{system?.network.received || 0} MB</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // ---- MODALS ----

  const renderModal = () => {
    if (!activeModal) return null;

    switch (activeModal) {
      case 'unique-visitors':
        return (
          <Modal title="Unique Visitors" onClose={() => setActiveModal(null)}>
            <div className="modal-stats-row">
              <div className="modal-stat"><span className="modal-stat-value">{analytics?.uniqueVisitors || 0}</span><span className="modal-stat-label">Total Unique</span></div>
              <div className="modal-stat"><span className="modal-stat-value">{analytics?.newVisitors || 0}</span><span className="modal-stat-label">New</span></div>
              <div className="modal-stat"><span className="modal-stat-value">{analytics?.returningVisitors || 0}</span><span className="modal-stat-label">Returning</span></div>
            </div>
            {analytics?.dailyVisits && analytics.dailyVisits.length > 0 && (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={analytics.dailyVisits.map(d => ({ ...d, date: formatDate(d.date) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                  <YAxis stroke="#64748b" fontSize={11} />
                  <Tooltip {...tooltipStyle} />
                  <Line type="monotone" dataKey="count" stroke="#40916C" strokeWidth={2} dot={{ fill: '#40916C' }} name="Visits" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Modal>
        );

      case 'page-views':
        return (
          <Modal title="Page Views" onClose={() => setActiveModal(null)}>
            <div className="modal-stats-row">
              <div className="modal-stat"><span className="modal-stat-value">{analytics?.totalPageViews || 0}</span><span className="modal-stat-label">Total Views</span></div>
              <div className="modal-stat"><span className="modal-stat-value">{analytics?.todayViews || 0}</span><span className="modal-stat-label">Today</span></div>
              <div className="modal-stat">
                <span className="modal-stat-value">
                  {analytics && analytics.uniqueVisitors > 0
                    ? (analytics.totalPageViews / analytics.uniqueVisitors).toFixed(1)
                    : '0'}
                </span>
                <span className="modal-stat-label">Views/Visitor</span>
              </div>
            </div>
            <h4 className="modal-chart-title">Hourly Distribution</h4>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="hour" stroke="#64748b" fontSize={10} interval={3} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="count" fill="#2D6A4F" name="Views" />
              </BarChart>
            </ResponsiveContainer>
          </Modal>
        );

      case 'new-vs-returning':
        return (
          <Modal title="New vs Returning Visitors" onClose={() => setActiveModal(null)}>
            <div className="modal-stats-row">
              <div className="modal-stat"><span className="modal-stat-value">{newPercent}%</span><span className="modal-stat-label">New</span></div>
              <div className="modal-stat"><span className="modal-stat-value">{100 - newPercent}%</span><span className="modal-stat-label">Returning</span></div>
              <div className="modal-stat"><span className="modal-stat-value">{analytics?.returningVisitors || 0}</span><span className="modal-stat-label">Return Visits</span></div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={newVsReturning()} cx="50%" cy="50%" outerRadius={85} dataKey="value" label={({ name, percent }) => `${name}: ${Math.round(percent * 100)}%`}>
                  {newVsReturning().map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip {...tooltipStyle} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Modal>
        );

      case 'avg-session':
        return (
          <Modal title="Session Duration" onClose={() => setActiveModal(null)}>
            <div className="modal-stats-row">
              <div className="modal-stat"><span className="modal-stat-value">{formatDuration(engagement?.avgSessionDuration || 0)}</span><span className="modal-stat-label">Average</span></div>
              <div className="modal-stat"><span className="modal-stat-value">{engagement?.totalSessions || 0}</span><span className="modal-stat-label">Total Sessions</span></div>
              <div className="modal-stat">
                <span className="modal-stat-value">
                  {engagement ? `${Math.round(((engagement.sessionDurationBuckets[2]?.count || 0) + (engagement.sessionDurationBuckets[3]?.count || 0)) / Math.max(engagement.totalSessions, 1) * 100)}%` : '0%'}
                </span>
                <span className="modal-stat-label">Engaged (2m+)</span>
              </div>
            </div>
            {engagement && engagement.sessionDurationBuckets.some(b => b.count > 0) && (
              <>
                <h4 className="modal-chart-title">Duration Distribution</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={engagement.sessionDurationBuckets}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={11} />
                    <Tooltip {...tooltipStyle} />
                    <Bar dataKey="count" fill="#40916C" name="Sessions" />
                  </BarChart>
                </ResponsiveContainer>
              </>
            )}
          </Modal>
        );

      case 'scroll-depth':
        return (
          <Modal title="Scroll Depth" onClose={() => setActiveModal(null)}>
            <div className="modal-stats-row">
              <div className="modal-stat"><span className="modal-stat-value">{engagement?.scrollDepthAvg || 0}%</span><span className="modal-stat-label">Average</span></div>
              {engagement?.scrollBreakdown.map(s => (
                <div key={s.depth} className="modal-stat">
                  <span className="modal-stat-value">{s.count}</span>
                  <span className="modal-stat-label">Reached {s.label}</span>
                </div>
              ))}
            </div>
            {engagement && engagement.scrollBreakdown.some(s => s.count > 0) && (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={engagement.scrollBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={11} />
                  <Tooltip {...tooltipStyle} />
                  <Bar dataKey="count" fill="#2D6A4F" name="Reached" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Modal>
        );

      case 'photo-views':
        return (
          <Modal title="Photo Views" onClose={() => setActiveModal(null)}>
            <div className="modal-stats-row">
              <div className="modal-stat">
                <span className="modal-stat-value">{engagement?.photoStats.reduce((a, b) => a + b.views, 0) || 0}</span>
                <span className="modal-stat-label">Total Photo Views</span>
              </div>
              <div className="modal-stat">
                <span className="modal-stat-value">{engagement?.photoStats.length || 0}</span>
                <span className="modal-stat-label">Photos Tracked</span>
              </div>
              <div className="modal-stat">
                <span className="modal-stat-value">
                  {engagement?.photoStats.length ? [...engagement.photoStats].sort((a, b) => b.views - a.views)[0]?.views : '-'}
                </span>
                <span className="modal-stat-label">Most Viewed (views)</span>
              </div>
            </div>
            {engagement && engagement.photoStats.length > 0 && (
              <div className="photo-views-grid photo-views-grid--modal">
                {engagement.photoStats.map((p, i) => (
                  <div key={p.photoFilename} className="photo-views-item">
                    <div className="photo-views-rank">#{i + 1}</div>
                    <img
                      src={`${apiBase}/uploads/${p.photoFilename}`}
                      alt={p.photoFilename}
                      className="photo-views-thumb"
                    />
                    <div className="photo-views-badge">
                      <span className="photo-views-badge-views">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="badge-icon"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        {p.views}
                      </span>
                      <span className="photo-views-badge-sep" />
                      <span className="photo-views-badge-duration">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="badge-icon"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        {formatAvgDuration(p.totalDuration ?? 0)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Modal>
        );

      default:
        return null;
    }
  };

  return (
    <div className="analytics-dashboard">
      <div className="analytics-header">
        <h2>Analytics</h2>
        <div className="date-range-selector">
          {([7, 30, 0] as const).map(d => (
            <button key={d} className={dateRange === d ? 'active' : ''} onClick={() => setDateRange(d)}>
              {d === 7 ? '7 Days' : d === 30 ? '30 Days' : 'All Time'}
            </button>
          ))}
        </div>
      </div>

      <div className="analytics-tabs">
        {(['overview', 'engagement', 'traffic', 'visitors', 'system'] as AnalyticsTab[]).map(tab => (
          <button
            key={tab}
            className={`analytics-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <div className="analytics-tab-content">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'engagement' && renderEngagementTab()}
        {activeTab === 'traffic' && renderTrafficTab()}
        {activeTab === 'visitors' && renderVisitorsTab()}
        {activeTab === 'system' && renderSystemTab()}
      </div>

      {renderModal()}
    </div>
  );
};

export default AnalyticsDashboard;
